import styled from '@emotion/styled';
import {
  NTA_COLORS,
  NTA_FONT_ARIAL_BLACK,
  NTA_PAGE_PADDING_X
} from '../constants';
import {
  ntaBodyBoldCss,
  ntaButtonCss,
  ntaIntroCss,
  ntaPageCss
} from '../typography';

export const Page = styled.div`
  width: 100vw;
  height: 100vh;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  background: ${NTA_COLORS.white};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  ${ntaPageCss}
`;

export const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px ${NTA_PAGE_PADDING_X} 24px;
  box-sizing: border-box;
`;

export const Inner = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const TitleRow = styled.div`
  position: relative;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Title = styled.h1`
  margin: 0;
  text-align: center;
  color: ${NTA_COLORS.textPrimary};
  ${ntaIntroCss}
  font-size: 24px;
  line-height: 24px;
  letter-spacing: 0;
`;

export const BackButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  width: 105px;
  height: 40px;
  padding: 0 12px;
  border: none;
  background: ${NTA_COLORS.resultBack};
  color: ${NTA_COLORS.white};
  cursor: pointer;
  box-sizing: border-box;
  ${ntaButtonCss}

  &:focus-visible {
    outline: 2px solid ${NTA_COLORS.accentBlue};
    outline-offset: 2px;
  }
`;

export const Score = styled.div`
  text-align: center;
  color: ${NTA_COLORS.textPrimary};
  --nta-font-family: ${NTA_FONT_ARIAL_BLACK};
  font-size: 16px;
  line-height: 32px;
  letter-spacing: 0;
`;

export const Tables = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

export const TableWrap = styled.div`
  border: 1px solid ${NTA_COLORS.resultDivider};
  width: 100%;
  padding: 4px 0;
  box-sizing: border-box;
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 154px 1fr 154px;
  width: 100%;
`;

export const QuestionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  width: 100%;
`;

export const QuestionLoaderCell = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 12px;
  background: ${NTA_COLORS.white};
  color: ${NTA_COLORS.resultLabel};
  ${ntaPageCss}
  font-size: 14px;
  line-height: 20px;
  text-align: center;
`;

const cellBorderRight = `border-right: 1px solid ${NTA_COLORS.resultDivider};`;

export const SummaryLabelCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: ${NTA_COLORS.white};
  border-bottom: 1px solid ${NTA_COLORS.resultDivider};
  ${cellBorderRight}
  color: ${NTA_COLORS.resultLabel};
  ${ntaPageCss}
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0;
  text-align: center;

  &:nth-child(4n) {
    border-right: none;
  }

  &:nth-last-child(-n + 4) {
    border-bottom: none;
  }
`;

export const SummaryValueCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: ${NTA_COLORS.white};
  border-bottom: 1px solid ${NTA_COLORS.resultDivider};
  ${cellBorderRight}
  color: ${NTA_COLORS.textPrimary};
  ${ntaPageCss}
  ${ntaBodyBoldCss}
  font-size: 16px;
  line-height: 24px;
  text-align: center;

  &:nth-child(4n) {
    border-right: none;
  }

  &:nth-last-child(-n + 4) {
    border-bottom: none;
  }
`;

export const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: ${NTA_COLORS.white};
  border-bottom: 1px solid ${NTA_COLORS.resultDivider};
  ${cellBorderRight}
  color: ${NTA_COLORS.textPrimary};
  ${ntaPageCss}
  ${ntaBodyBoldCss}
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0;

  &:last-child {
    border-right: none;
  }
`;

export const BodyCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: ${NTA_COLORS.white};
  border-bottom: ${({ $hideBottomBorder }) =>
    $hideBottomBorder ? 'none' : `1px solid ${NTA_COLORS.resultDivider}`};
  ${cellBorderRight}
  color: ${NTA_COLORS.resultLabel};
  ${ntaPageCss}
  font-size: 16px;
  line-height: 24px;
  text-align: center;

  &:last-child {
    border-right: none;
  }
`;

const statusPillStyles = {
  wrong: {
    background: NTA_COLORS.statusWrong,
    color: NTA_COLORS.white
  },
  correct: {
    background: NTA_COLORS.statusCorrect,
    color: NTA_COLORS.white
  },
  na: {
    background: 'transparent',
    color: NTA_COLORS.resultLabel
  }
};

export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  background: ${({ $type }) =>
    statusPillStyles[$type]?.background ?? statusPillStyles.na.background};
  color: ${({ $type }) =>
    statusPillStyles[$type]?.color ?? statusPillStyles.na.color};
  ${ntaBodyBoldCss}
  font-size: 16px;
  line-height: 12px;
  letter-spacing: 0;
  text-transform: uppercase;
`;
